import { PriceBounds } from "@/modules/base_price/utils/dayPricing";

/**
 * "Define average CPM": finds the fill and price for every month that make
 * the year average a given CPM while every month still earns exactly its
 * budget.
 *
 * The year's average is weighted — total revenue ÷ total sellable ÷ 1000 — and
 * with every month on budget the revenue is fixed, so an average is really a
 * total to sell: (total budget ÷ target) × 1000 impressions. Every month's
 * sellable is scaled by one factor to reach it, so every price moves by that
 * same factor and the months keep their pattern (a month twice as expensive
 * as another stays twice as expensive). It is deliberately not a flat price:
 * a flat price would put every month at the same CPM and push the ones that
 * cannot sell enough off budget.
 *
 * A month can sell no more than all of its impressions, and the target's
 * price bounds limit how far its price may move; a month that hits either
 * holds there and the others make up the difference. When even that cannot
 * reach the target, the result is the closest the year can get.
 */

export type AverageCpmMonth = {
	gross: number;
	budget: number;
	/** What the month sells now — the pattern the result keeps. */
	sellable: number;
};

export type AverageCpmResult = {
	/** Per month, in the order given; null for a month with nothing to price (no budget or no impressions). */
	months: ({ fill: number; cpm: number; sellable: number } | null)[];
	/** The average the result actually lands on. */
	achieved: number;
	/** Did it land on the target, or only as close as the year allows? */
	reached: boolean;
};

const ITERATIONS = 80;

export const solveAverageCpm = (
	months: AverageCpmMonth[],
	target: number,
	bounds: PriceBounds
): AverageCpmResult => {
	const priceable = months.map((month) => month.budget > 0 && month.gross > 0);

	/**
	 * How much a month may sell while still earning exactly its budget: never
	 * more than all its impressions, and only as much as keeps its price inside
	 * the bounds (a higher price means selling less, and the reverse).
	 */
	const range = months.map((month, i) => {
		if (!priceable[i]) return { low: 0, high: 0 };

		const atMax = bounds.max !== null && bounds.max > 0 ? (month.budget * 1000) / bounds.max : 0;
		const atMin = bounds.min !== null && bounds.min > 0 ? (month.budget * 1000) / bounds.min : Infinity;
		const high = Math.min(month.gross, atMin);

		return { low: Math.min(atMax, high), high };
	});

	// A month selling nothing has no pattern to keep: it starts from all of
	// its impressions, the only shape it has.
	const base = months.map((month, i) =>
		!priceable[i] ? 0 : month.sellable > 0 ? month.sellable : month.gross
	);

	const sellableAt = (factor: number) =>
		base.map((value, i) => Math.min(range[i].high, Math.max(range[i].low, value * factor)));

	const total = (values: number[]) => values.reduce((sum, value) => sum + value, 0);

	const budget = total(months.map((month, i) => (priceable[i] ? month.budget : 0)));
	const wanted = target > 0 ? (budget * 1000) / target : 0;

	// Total sellable only grows with the factor, so the factor that lands on it
	// is found by halving the interval it can lie in.
	let low = 0;
	let high = 1;

	while (total(sellableAt(high)) < wanted && high < 1e9) high *= 2;

	for (let i = 0; i < ITERATIONS; i++) {
		const mid = (low + high) / 2;

		if (total(sellableAt(mid)) < wanted) {
			low = mid;
		} else {
			high = mid;
		}
	}

	const sellable = sellableAt(high);
	const sold = total(sellable);
	const achieved = sold > 0 ? budget / (sold / 1000) : 0;

	return {
		months: months.map((month, i) =>
			!priceable[i] || sellable[i] <= 0
				? null
				: {
						sellable: sellable[i],
						fill: (sellable[i] / month.gross) * 100,
						cpm: month.budget / (sellable[i] / 1000),
					}
		),
		achieved,
		reached: Math.abs(achieved - target) <= 0.005,
	};
};
